<template>
  <ModalComponent
    ref="modal"
    submitButtonText="Close"
    :hideSubmitButton="true"
    size="lg"
  >
    <template #title>
      {{ title }}
    </template>
    <template #body>
      <!-- Show Companies List -->
      <div v-if="mode === 'companies'">
        <h6>Companies ({{ companies?.length || 0 }})</h6>
        <div class="table-responsive">
          <table class="table table-sm">
            <thead>
              <tr>
                <th>Company</th>
                <th>Company ID</th>
                <th>Registry Code</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="company in companies" :key="company.id">
                <td>
                  <strong>{{ company.name }}</strong>
                </td>
                <td>{{ company.id }}</td>
                <td>{{ company.registry_code || "N/A" }}</td>
                <td>
                  <button
                    class="btn btn-sm btn-outline-secondary"
                    @click="viewCompanyCertificates(company.id)"
                  >
                    <font-awesome-icon icon="list" /> View Details
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Show Certificates Table -->
      <div v-if="mode === 'certificates'">
        <h6>Certificates ({{ portfolioItems?.length || 0 }})</h6>
        <div
          class="table-responsive"
          style="max-height: 400px; overflow-y: auto"
        >
          <table class="table table-sm">
            <thead>
              <tr>
                <th>PI ID</th>
                <th>Cert ID</th>
                <th>Count</th>
                <th>Production Month</th>
                <th>Technology</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in portfolioItems" :key="item.portfolio_item_id">
                <td>{{ item.portfolio_item_id }}</td>
                <td>{{ item.certificate_id }}</td>
                <td>{{ item.count }} MWh</td>
                <td>{{ formatProductionMonth(item.production_month) }}</td>
                <td>{{ item.technology_code }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>
  </ModalComponent>

  <!-- Nested Certificates Modal (when viewing from companies list) -->
  <ModalComponent
    v-if="showNestedPortfolioItems"
    ref="nestedModal"
    submitButtonText="Close"
    :hideSubmitButton="true"
    size="lg"
  >
    <template #title> {{ nestedCompanyName }} Certificates </template>
    <template #body>
      <h6>Certificates ({{ nestedPortfolioItems.length }})</h6>
      <div class="table-responsive" style="max-height: 400px; overflow-y: auto">
        <table class="table table-sm">
          <thead>
            <tr>
              <th>PI ID</th>
              <th>Cert ID</th>
              <th>Count</th>
              <th>Production Month</th>
              <th>Technology</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in nestedPortfolioItems"
              :key="item.portfolio_item_id"
            >
              <td>{{ item.portfolio_item_id }}</td>
              <td>{{ item.certificate_id }}</td>
              <td>{{ item.count }} MWh</td>
              <td>{{ formatProductionMonth(item.production_month) }}</td>
              <td>{{ item.technology_code }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import ModalComponent from "@/components/common/ModalComponent.vue";
import { formatMonthYear } from "@/composables/formatUtils";
import { nextTick, ref, useTemplateRef, watch } from "vue";

const props = defineProps<{
  title: string;
  mode: "companies" | "certificates";
  companies?: Array<{
    id: number;
    name: string;
    registry_code?: string | null;
  }>;
  portfolioItems?: Array<{
    portfolio_item_id: number;
    certificate_id: number;
    count: number;
    production_month: string;
    technology_code: string;
    company_id?: number;
  }>;
  companyPortfolioItemsMap?: Record<number, any[]>;
}>();

const modal = useTemplateRef<InstanceType<typeof ModalComponent>>("modal");
const nestedModal =
  useTemplateRef<InstanceType<typeof ModalComponent>>("nestedModal");

const showNestedPortfolioItems = ref(false);
const nestedPortfolioItems = ref<any[]>([]);
const nestedCompanyName = ref("");

const formatProductionMonth = (month: string) => {
  try {
    return formatMonthYear(month);
  } catch {
    return month;
  }
};

const viewCompanyCertificates = async (companyId: number) => {
  const company = props.companies?.find((c) => c.id === companyId);
  if (!company) return;

  nestedCompanyName.value = company.name;

  if (props.companyPortfolioItemsMap) {
    nestedPortfolioItems.value =
      props.companyPortfolioItemsMap[companyId] || [];
  } else if (props.portfolioItems) {
    nestedPortfolioItems.value = props.portfolioItems.filter(
      (item) => item.company_id === companyId
    );
  } else {
    nestedPortfolioItems.value = [];
  }

  showNestedPortfolioItems.value = true;
  await nextTick();
  nestedModal.value?.toggleModal();
};

// Toggle modal from parent
const toggleModal = () => {
  modal.value?.toggleModal();
};

watch(showNestedPortfolioItems, (show) => {
  if (!show) {
    nestedPortfolioItems.value = [];
    nestedCompanyName.value = "";
  }
});

defineExpose({
  toggleModal,
});
</script>
